import request, { type PageResult } from './request';
import { mapJob, mapEnterprise, mapPolicy, mapNotice, mapJobFair, mapSchool, mapTalent } from './mapper';

// --- 公开接口（无需登录） ---

export const jobApi = {
  list: (params: { page?: number; size?: number; keyword?: string; enterprise_id?: number; province?: string; city?: string; district?: string; town?: string; major?: string; salary_min?: number; salary_max?: number }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/jobs/public', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapJob),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/jobs/public/${id}`).then(mapJob),
};

export const policyApi = {
  listPublic: (params: { page?: number; size?: number; category_id?: number; user_type?: string; identity_type?: string; policy_type?: string; keyword?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/policies/public', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapPolicy),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/policies/public/${id}`).then(mapPolicy),
  listCategoriesPublic: (params?: { user_type?: string }) =>
    request.get<unknown, Array<Record<string, unknown>>>('/policy-categories/public', { params }),
};

export const noticeApi = {
  listPublic: (params: { page?: number; size?: number; category_id?: number; keyword?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/notices/public', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapNotice),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/notices/public/${id}`).then(mapNotice),
  listCategoriesPublic: () => request.get<unknown, Array<Record<string, unknown>>>('/notice-categories/public'),
};

export const jobFairApi = {
  listPublic: (params: { page?: number; size?: number; status?: string; keyword?: string; scope?: string; org_type?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/job-fairs/public', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapJobFair),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/job-fairs/public/${id}`).then(mapJobFair),
  landing: (registrationId: number) => request.get<unknown, Record<string, unknown>>(`/job-fairs/landing/${registrationId}`),
  register: (id: number, data: { org_name: string; jobs: string; headcount: number; contact_phone?: string }) =>
    request.post<Record<string, unknown>, unknown>(`/job-fairs/${id}/register`, data),
};

export const trainingApi = {
  listPublic: (params: { page?: number; size?: number }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/training/public', { params }),
};

export const dashboardApi = {
  getPublicStats: () => request.get<unknown, Record<string, unknown>>('/dashboard/public-stats'),
  getPublicDataScreen: () => request.get<unknown, Record<string, unknown>>('/dashboard/public/data-screen'),
  getPublicProvinceDistribution: () => request.get<unknown, PageResult<Record<string, unknown>>>('/dashboard/public/province-distribution'),
  getPublicJobCategories: () => request.get<unknown, { label: string; count: number }[]>('/dashboard/public/job-categories'),
  getPublicJobTrend: () => request.get<unknown, { date: string; count: number }[]>('/dashboard/public/job-trend'),
  getPublicHotJobs: () => request.get<unknown, { name: string; salary: string; applied_count: number }[]>('/dashboard/public/hot-jobs'),
};

// --- 需要登录的接口 ---

export interface SmsCodeResponse {
  mock?: boolean;
  code?: string;
  message?: string;
}

export interface LoginResponse {
  token: string;
  refresh_token?: string;
  role: string;
  user_id?: number;
  audit_status?: string;
  audit_note?: string;
  phone?: string;
  name?: string;
  nickname?: string;
}

export const authApi = {
  login: (data: { phone: string; password: string }) =>
    request.post<Record<string, unknown>, LoginResponse>('/auth/login', data),
  smsLogin: (data: { phone: string; code: string }) =>
    request.post<Record<string, unknown>, LoginResponse>('/auth/sms-login', data),
  register: (data: { phone: string; password: string; role: string; sms_code: string }) =>
    request.post<Record<string, unknown>, unknown>('/auth/register', data),
  sendSmsCode: (data: { phone: string; purpose?: string }) =>
    request.post<Record<string, unknown>, SmsCodeResponse>('/auth/send-sms-code', data),
  logout: () => request.post('/auth/logout'),
  changePhone: (data: { phone: string; code: string }) =>
    request.post<Record<string, unknown>, unknown>('/auth/bind-phone', data),
  changePassword: (data: { old_password: string; new_password: string }) =>
    request.put<Record<string, unknown>, unknown>('/auth/change-password', data),
  forgotPassword: (data: { phone: string }) =>
    request.post<Record<string, unknown>, SmsCodeResponse>('/auth/forgot-password', data),
  verifyCode: (data: { phone: string; code: string }) =>
    request.post<Record<string, unknown>, unknown>('/auth/verify-code', data),
  resetPassword: (data: { phone: string; code: string; new_password: string }) =>
    request.post<Record<string, unknown>, unknown>('/auth/reset-password', data),
};

export const enterpriseApi = {
  list: (params: { page?: number; size?: number; keyword?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/enterprises/public', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapEnterprise),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/enterprises/public/${id}`).then(mapEnterprise),
};

export const schoolApi = {
  list: (params: { page?: number; size?: number; keyword?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/schools/public', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapSchool),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/schools/public/${id}`).then(mapSchool),
};

export const talentApi = {
  list: (params: { page?: number; size?: number; major?: string; education?: string; keyword?: string; skill?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/talents', { params }).then((res) => ({
      ...res,
      list: (res?.list || []).map(mapTalent),
    })),
  getById: (id: number) => request.get<unknown, Record<string, unknown>>(`/talents/${id}`).then(mapTalent),
  getResume: (id: number) => request.get<unknown, Record<string, unknown>>(`/students/${id}/resume/public`),
};

export const studentApi = {
  getProfile: () => request.get<unknown, Record<string, unknown>>('/students/profile'),
  getResume: () => request.get<unknown, Record<string, unknown>>('/students/resume'),
  getResumes: () => request.get<unknown, Record<string, unknown>>('/students/resumes'),
  updateProfile: (data: Record<string, unknown>) => request.put<Record<string, unknown>, unknown>('/students/profile', data),
};

function normalizeResumeList(payload: unknown): any[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const data = payload as Record<string, any>;
    return data.list || data.items || data.resumes || data.data || [];
  }
  return [];
}

function normalizeResumeIdValue(resume: any): number | undefined {
  const raw = resume?.id ?? resume?.resume_id ?? resume?.resumeId;
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
}

export const applicationApi = {
  apply: async (data: { job_id: number; resume_id?: number | string }) => {
    let resolvedResumeId: number | undefined;
    if (data.resume_id !== undefined && data.resume_id !== null && data.resume_id !== '') {
      const numeric = Number(data.resume_id);
      resolvedResumeId = Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
    } else {
      try {
        const payload = await studentApi.getResumes();
        const list = normalizeResumeList(payload);
        if (list.length > 0) {
          const preferred = list.find((item: any) => item?.is_default || item?.isDefault || item?.default) || list[0];
          resolvedResumeId = normalizeResumeIdValue(preferred);
        }
      } catch {
        try {
          const single: any = await request.get('/students/resume');
          resolvedResumeId = normalizeResumeIdValue(single);
        } catch {
          resolvedResumeId = undefined;
        }
      }
    }
    const payload: Record<string, unknown> = { job_id: data.job_id };
    if (resolvedResumeId) payload.resume_id = resolvedResumeId;
    return request.post<Record<string, unknown>, unknown>('/applications', payload);
  },
  listMine: (params: { page?: number; size?: number; status?: string; status_group?: string }) =>
    request.get<unknown, PageResult<Record<string, unknown>>>('/applications/student', { params }),
  withdraw: (id: number) => request.put<Record<string, unknown>, unknown>(`/applications/${id}/withdraw`),
};

export const favoriteApi = {
  addFavorite: (jobId: number) => request.post('/favorites', { job_id: jobId }),
  removeFavorite: (jobId: number) => request.delete(`/favorites/${jobId}`),
  checkFavorite: (jobId: number) => request.get<unknown, { favorited: boolean }>(`/favorites/${jobId}/check`),
};

export const entrepreneurshipApi = {
  listPublic: (params: { page?: number; size?: number; keyword?: string; category?: string }) =>
    request.get<unknown, any>('/entrepreneurship/public', { params }),
  getPublicById: (id: number | string) =>
    request.get<unknown, any>(`/entrepreneurship/public/${id}`),
};

export const pageApi = {
  getBySlug: (slug: string) => request.get<unknown, Record<string, unknown>>(`/pages/slug/${slug}`),
};
